import React from 'react';
import {
  Scissors, Sprout, Shovel, Layers, Flower2, Moon, Sun,
  Leaf, Droplets, CheckCircle2, Circle, Pencil, Trash2, ClipboardList,
} from 'lucide-react';

export const TYPE_ICON = {
  prune:           Scissors,
  fertilise:       Sprout,
  divide:          Shovel,
  mulch:           Layers,
  bloom:           Flower2,
  dormancy_start:  Moon,
  dormancy_end:    Sun,
  seasonal:        Leaf,
  water:           Droplets,
  custom:          ClipboardList,
};

export const TYPE_TONE = {
  prune:           'bg-amber-100 text-amber-700',
  fertilise:       'bg-emerald-100 text-emerald-700',
  divide:          'bg-violet-100 text-violet-700',
  mulch:           'bg-orange-100 text-orange-700',
  bloom:           'bg-pink-100 text-pink-700',
  dormancy_start:  'bg-slate-200 text-slate-700',
  dormancy_end:    'bg-sky-100 text-sky-700',
  seasonal:        'bg-lime-100 text-lime-700',
  water:           'bg-blue-100 text-blue-700',
  custom:          'bg-garden-primary/10 text-garden-primary',
};

// Renders a single task row. Shared between the Tasks screen and the
// "Upcoming tasks" section on plant detail.
//
// - showPlantName: include the small plant-name footer with arrow.
// - onOpenPlant: if provided, the whole row becomes clickable (navigates to
//   the plant). The checkbox always stops propagation so toggling done
//   doesn't navigate away.
export default function TaskRow({
  task,
  busy,
  onToggle,
  onOpenPlant,
  onEdit,
  onDelete,
  showPlantName = true,
}) {
  const Icon = TYPE_ICON[task.type] || Leaf;
  const tone = TYPE_TONE[task.type] || 'bg-garden-primary/10 text-garden-primary';
  const isInfo = !!task.is_informational;

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggle?.();
  };

  const rowClickable = !!onOpenPlant;

  const handleRowClick = () => {
    if (rowClickable) onOpenPlant();
  };

  const handleKey = (e) => {
    if (!rowClickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenPlant();
    }
  };

  return (
    <li
      role={rowClickable ? 'button' : undefined}
      tabIndex={rowClickable ? 0 : -1}
      onClick={handleRowClick}
      onKeyDown={handleKey}
      className={
        'flex gap-3 px-3 py-2.5 items-start transition-colors ' +
        (rowClickable
          ? 'cursor-pointer hover:bg-garden-primary/5 focus:bg-garden-primary/5 focus:outline-none'
          : '')
      }
    >
      {!isInfo ? (
        <button
          onClick={handleCheckboxClick}
          disabled={busy}
          aria-label={task.completed ? 'Mark as not done' : 'Mark as done'}
          className="mt-0.5 min-w-9 min-h-9 flex items-center justify-center rounded-full text-garden-primary active:scale-95 transition disabled:opacity-50"
        >
          {task.completed
            ? <CheckCircle2 size={24} />
            : <Circle size={24} className="text-garden-ink/40" />}
        </button>
      ) : (
        <span className="mt-0.5 min-w-9 min-h-9 flex items-center justify-center" aria-hidden>
          <span className={`w-8 h-8 rounded-full flex items-center justify-center ${tone}`}>
            <Icon size={16} />
          </span>
        </span>
      )}

      <div className="flex-1 min-w-0 pt-1">
        <div className="flex items-center gap-1.5">
          {!isInfo ? (
            <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${tone}`}>
              <Icon size={12} />
            </span>
          ) : null}
          <span
            className={
              'text-sm font-medium ' +
              (task.completed ? 'line-through text-garden-muted' : 'text-garden-ink')
            }
          >
            {task.label}
          </span>
        </div>
        {task.notes ? (
          <div className="mt-0.5 text-xs text-garden-muted line-clamp-2">
            {task.notes}
          </div>
        ) : null}
        {showPlantName && task.plant_name ? (
          <div className="mt-1 text-[11px] text-garden-primary/80">
            {task.plant_name} {'\u2192'}
          </div>
        ) : null}
      </div>

      {task.is_custom && (onEdit || onDelete) ? (
        <div className="flex items-center gap-1 shrink-0">
          {onEdit ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(task); }}
              aria-label="Edit task"
              className="min-w-9 min-h-9 flex items-center justify-center rounded-full text-garden-muted hover:bg-black/5 active:scale-95 transition"
            >
              <Pencil size={16} />
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(task); }}
              aria-label="Delete task"
              className="min-w-9 min-h-9 flex items-center justify-center rounded-full text-garden-muted hover:bg-red-50 hover:text-red-600 active:scale-95 transition"
            >
              <Trash2 size={16} />
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
